/** Type declarations for the Funway SDK (loaded externally via script tag) */
interface FunwaySDKType {
  init(options?: { debug?: boolean }): Promise<void>;
  gameReady(): void;
  on(event: string, callback: (...args: unknown[]) => void): void;
  saveGame(data: Record<string, unknown>): Promise<void>;
  loadGame(): Promise<Record<string, unknown> | null>;
  submitScore(score: number): Promise<{ isPersonalBest: boolean }>;
  completeDailyChallenge(): Promise<{ streak: number }>;
  share(options: { text: string; score?: number }): Promise<void>;
  hasCompletedToday(): Promise<boolean>;
  openLeaderboard(): void;
  isInPortal(): boolean;
  generateShareText(
    grid: string[][],
    options: { title: string; attempts: number; maxAttempts: number },
  ): string;
}

declare const FunwaySDK: FunwaySDKType | undefined;

/** Safely retrieve the SDK instance, or null if not loaded. */
function getSDK(): FunwaySDKType | null {
  if (typeof FunwaySDK !== 'undefined') return FunwaySDK;
  return null;
}

/** Check if the SDK is available */
export function isSDKAvailable(): boolean {
  return getSDK() !== null;
}

/** Initialize the SDK. No-op if not available. */
export async function initSDK(): Promise<void> {
  const sdk = getSDK();
  if (!sdk) return;
  try {
    await sdk.init({ debug: import.meta.env.DEV });
  } catch {
    console.warn('[funway] SDK init failed');
  }
}

/** Signal that the game is ready to play */
export function signalGameReady(): void {
  const sdk = getSDK();
  if (!sdk) return;
  sdk.gameReady();
}

/** Register a handler for the pause event */
export function onPause(callback: () => void): void {
  const sdk = getSDK();
  if (!sdk) return;
  sdk.on('pause', callback);
}

/** Register a handler for the resume event */
export function onResume(callback: () => void): void {
  const sdk = getSDK();
  if (!sdk) return;
  sdk.on('resume', callback);
}

/** Register a handler for the mute event */
export function onMute(callback: (muted: boolean) => void): void {
  const sdk = getSDK();
  if (!sdk) return;
  sdk.on('mute', callback as (...args: unknown[]) => void);
}

/** Save game progress to the SDK (cloud storage) */
export async function saveGameProgress(
  data: Record<string, unknown>,
): Promise<void> {
  const sdk = getSDK();
  if (!sdk) return;
  try {
    await sdk.saveGame(data);
  } catch {
    // SDK save failed — localStorage fallback already handled elsewhere
  }
}

/** Load game progress from the SDK (cloud storage) */
export async function loadGameProgress(): Promise<Record<string, unknown> | null> {
  const sdk = getSDK();
  if (!sdk) return null;
  try {
    return await sdk.loadGame();
  } catch {
    return null;
  }
}

/** Submit score to the Funway leaderboard */
export async function submitScore(score: number): Promise<boolean> {
  const sdk = getSDK();
  if (!sdk) return false;
  try {
    const result = await sdk.submitScore(score);
    return result.isPersonalBest;
  } catch {
    return false;
  }
}

/** Mark the daily challenge as complete */
export async function completeDailyChallenge(): Promise<number> {
  const sdk = getSDK();
  if (!sdk) return 0;
  try {
    const result = await sdk.completeDailyChallenge();
    return result.streak;
  } catch {
    return 0;
  }
}

/** Share result via the Funway SDK */
export async function shareViaSDK(
  text: string,
  score?: number,
): Promise<boolean> {
  const sdk = getSDK();
  if (!sdk) return false;
  try {
    await sdk.share({ text, score });
    return true;
  } catch {
    return false;
  }
}
